import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Github, ArrowRight, Sparkles } from "lucide-react";

const HOME_LIMIT = 6;

export default function Projects({ projects = [] }) {
    // Prioritise featured then by order — show top N on home
    const sorted = [...projects].sort((a, b) => {
        if ((b.featured ? 1 : 0) !== (a.featured ? 1 : 0)) return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        return (a.order || 0) - (b.order || 0);
    });
    const homeProjects = sorted.slice(0, HOME_LIMIT);
    const categories = ["All", ...Array.from(new Set(homeProjects.map((p) => p.category)))];
    const statuses = ["All status", ...Array.from(new Set(homeProjects.map((p) => p.status).filter(Boolean)))];
    const [active, setActive] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All status");
    const filtered = homeProjects.filter(
        (p) => (active === "All" || p.category === active) && (statusFilter === "All status" || p.status === statusFilter)
    );
    const hasMore = projects.length > HOME_LIMIT;

    const statusColor = (s) => {
        if (s === "Active") return "bg-emerald-500/15 border-emerald-500/40 text-emerald-300";
        if (s === "Maintenance") return "bg-amber-500/15 border-amber-500/40 text-amber-300";
        if (s === "Archived") return "bg-zinc-500/15 border-zinc-500/40 text-zinc-300";
        return "bg-white/5 border-white/15 text-ink-100"; // Completed
    };

    return (
        <section id="work" data-testid="projects-section" className="relative py-24 md:py-32 border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="mono-label mb-4">04 · Selected Work</div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tighter">
                            Engineered for <span className="text-brand">impact</span> & scale.
                        </h2>
                        <p className="mt-4 text-ink-200 max-w-xl">
                            Featured engineering projects and system implementations. Total {projects.length} projects delivered and in production.
                        </p>
                    </div>
                    <div className="flex flex-col gap-3 lg:max-w-md">
                        <div className="flex items-center gap-2 overflow-x-auto pb-1.5 scrollbar-none sm:flex-wrap sm:overflow-visible sm:pb-0" data-testid="projects-filter">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setActive(c)}
                                    data-testid={`project-filter-${c.toLowerCase().replace(/\s|\//g, "-")}`}
                                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                                        active === c
                                            ? "bg-brand border-brand text-white"
                                            : "border-white/10 text-ink-200 hover:border-white/30 hover:text-white"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto pb-1.5 scrollbar-none sm:flex-wrap sm:overflow-visible sm:pb-0" data-testid="projects-status-filter">
                            {statuses.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setStatusFilter(s)}
                                    data-testid={`project-status-${s.toLowerCase().replace(/\s/g, "-")}`}
                                    className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border transition-colors ${
                                        statusFilter === s
                                            ? "bg-white text-black border-white"
                                            : "border-white/10 text-ink-300 hover:text-white hover:border-white/30"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                    {filtered.map((p, i) => (
                        <Link
                            key={p.id || p.slug}
                            to={`/projects/${p.slug}`}
                            data-testid={`project-card-${p.slug}`}
                            className={`group relative overflow-hidden rounded-2xl border border-white/5 bg-ink-800/40 hover:border-brand/40 transition-all duration-300 hover:-translate-y-1 animate-in fade-in zoom-in-95 ${
                                p.featured && i === 0 ? "md:col-span-2" : ""
                            }`}
                        >
                            <div className={`relative overflow-hidden ${p.featured && i === 0 ? "aspect-[2.4/1]" : "aspect-[4/3]"} bg-ink-900`}>
                                {p.cover_image && (
                                    <img
                                        src={p.cover_image}
                                        alt={p.title}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
                                <div className="absolute top-5 left-5 flex flex-wrap items-center gap-2">
                                    <span className="font-mono text-xs px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-ink-100">
                                        {p.category}
                                    </span>
                                    {p.featured && (
                                        <span className="font-mono text-xs px-2.5 py-1 rounded-full bg-brand/90 text-white">
                                            Featured
                                        </span>
                                    )}
                                    {p.client?.includes("Kodekami") && (
                                        <span className="font-mono text-xs px-2.5 py-1 rounded-full bg-brand/25 border border-brand/50 text-brand-light backdrop-blur font-medium flex items-center gap-1 shadow-[0_0_12px_rgba(255,69,0,0.3)]">
                                            <Sparkles size={11} className="text-brand" /> Kodekami Ecosystem
                                        </span>
                                    )}
                                    {p.status && p.status !== "Completed" && (
                                        <span className={`font-mono text-[10px] px-2 py-1 rounded-full border ${statusColor(p.status)}`}>
                                            {p.status === "Active" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 align-middle animate-pulse" />}
                                            {p.status}
                                        </span>
                                    )}
                                </div>
                                <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ArrowUpRight size={18} />
                                </div>
                            </div>

                            <div className="p-6 lg:p-8 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="mono-label">
                                        {p.year} · {p.client?.includes("Kodekami") ? (
                                            <span className="text-brand font-medium">{p.client}</span>
                                        ) : (
                                            p.client
                                        )}
                                    </span>
                                    <div className="flex gap-2">
                                        {p.demo_url && (
                                            <span className="text-ink-300 group-hover:text-brand transition-colors">
                                                <ExternalLink size={14} />
                                            </span>
                                        )}
                                        {p.repo_url && (
                                            <span className="text-ink-300 group-hover:text-brand transition-colors">
                                                <Github size={14} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <h3 className="font-display text-2xl lg:text-3xl text-white tracking-tight leading-tight">
                                    {p.title}
                                </h3>
                                <p className="text-ink-200 leading-relaxed line-clamp-2">{p.summary}</p>
                                <div className="pt-2 flex flex-wrap gap-1.5">
                                    {p.tech_stack?.slice(0, 5).map((t) => (
                                        <span
                                            key={t}
                                            className="font-mono text-[10px] px-2 py-0.5 rounded bg-ink-700/60 border border-white/5 text-ink-100"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                    {p.tech_stack?.length > 5 && (
                                        <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-ink-700/60 border border-white/5 text-ink-300">
                                            +{p.tech_stack.length - 5}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="text-center py-16 text-ink-300">No projects in this category.</div>
                )}

                {hasMore && (
                    <div className="mt-12 flex justify-center" data-testid="view-all-wrap">
                        <Link
                            to="/work"
                            data-testid="view-all-projects"
                            className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/15 text-white font-medium hover:bg-white/5 transition-colors"
                        >
                            View all {projects.length} projects
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}
